import React, { useEffect, useRef } from 'react'
import classNames from 'classnames'
import { getTheme } from '../../common/ambient'
import useHighcharts from './useHighcharts'
import './HighchartsTidesGraph.less'

export default function HighchartsTidesGraph ({ className, data = [], height = 120, width = null, user }) {
  const chartRef = useRef(null)
  const chartInstanceRef = useRef(null)
  const highchartsLoaded = useHighcharts()

  useEffect(() => {
    // Ensure Highcharts is loaded
    if (!highchartsLoaded || !window.Highcharts || !chartRef.current || data.length === 0) {
      return
    }

    // Get CSS variable values
    const brandColor = getComputedStyle(document.documentElement)
      .getPropertyValue('--brandColor').trim()

    // Determine text color based on theme
    // Light mode: #313236 (@gray-dark-amb)
    // Dark mode: #f2f3f8 (--color-white)
    const textColor = getTheme(user) === 'dark' ? '#f2f3f8' : '#313236'
    const labelColorLight = getTheme(user) === 'dark' ? '#B1B5C3' : '#666'

    // Find interpolation point and calculate "now" value
    const now = Date.now() / 1000
    let nowPointValue = null
    let interpolationPoint = null
    let firstFuturePoint = null

    // Check if first point is the interpolation point
    if (data.length > 0 && data[0].isInterpolationPoint) {
      interpolationPoint = data[0]
      firstFuturePoint = data[1]

      if (firstFuturePoint) {
        // Interpolate the height value at current time
        const timeDiff = firstFuturePoint.time - interpolationPoint.time
        const timeProgress = (now - interpolationPoint.time) / timeDiff
        const valueDiff = firstFuturePoint.value - interpolationPoint.value
        nowPointValue = interpolationPoint.value + (valueDiff * timeProgress)
      }
    }

    // Transform data for Highcharts (skip interpolation point, add "now" point first)
    const values = []

    // Add "now" point as first point
    if (nowPointValue !== null) {
      values.push({
        x: now * 1000, // Convert to milliseconds
        y: nowPointValue,
        label: '',
        marker: {
          fillColor: brandColor || '#fed741',
          lineColor: brandColor || '#fed741',
          lineWidth: 2,
          radius: 3,
          symbol: 'circle'
        }
      })
    }

    // Add remaining tide points (skip interpolation point)
    data.forEach((d, index) => {
      if (d.isInterpolationPoint) return

      values.push({
        x: d.time * 1000, // Convert to milliseconds
        y: d.value,
        label: d.label,
        marker: {
          fillColor: 'white',
          lineColor: brandColor,
          lineWidth: 2,
          radius: 3,
          symbol: 'circle'
        }
      })
    })

    // Destroy existing chart if it exists
    if (chartInstanceRef.current) {
      chartInstanceRef.current.destroy()
    }

    // Create new chart
    chartInstanceRef.current = window.Highcharts.chart(chartRef.current, {
      chart: {
        type: 'areaspline',
        height: height,
        width: width,
        backgroundColor: 'transparent',
        margin: [30, 20, 20, 20],
        spacing: [0, 0, 0, 0]
      },
      title: {
        text: null
      },
      subtitle: {
        text: null
      },
      credits: {
        enabled: false
      },
      exporting: {
        enabled: false
      },
      legend: {
        enabled: false
      },
      xAxis: {
        type: 'datetime',
        gridLineWidth: 0,
        lineWidth: 0,
        lineColor: '#e0e0e0',
        tickWidth: 0,
        labels: {
          enabled: true,
          y: 13,
          formatter: function() {
            // Find the data point for this tick
            const point = values.find(v => v.x === this.value);
            return point ? point.label : '';
          },
          style: {
            color: labelColorLight,
            fontSize: '10px',
            fontWeight: '400'
          }
        },
        tickPositioner: function() {
          // Return all the x values as tick positions
          return values.map(v => v.x);
        }
      },
      yAxis: {
        title: {
          text: null
        },
        gridLineWidth: 0,
        labels: {
          enabled: false
        }
      },
      tooltip: {
        enabled: false
      },
      plotOptions: {
        areaspline: {
          dataLabels: {
            useHTML: true,
            align: 'center',
            crop: false,
            overflow: 'allow',
            y: -10,
            style: {
              textOutline: 'none'
            }
          },
          marker: {
            enabled: true
          },
          lineWidth: 2,
          states: {
            hover: {
              lineWidth: 2
            }
          },
          threshold: null
        }
      },
      series: [
        {
          name: 'Tide Height',
          data: values,
          lineColor: brandColor,
          fillColor: 'rgba(235, 247, 255, 0.5)',
          animation: false,
          enableMouseTracking: false,
          dataLabels: {
            enabled: true,
            formatter: function () {
              // Don't show label for "NOW" point
              if (this.point.label === 'NOW') {
                return null
              }

              // Find the corresponding data point
              const dataPoint = data.find(d => d.time * 1000 === this.point.x)
              if (!dataPoint) return null

              const tideType = dataPoint.type || ''
              const typeInitial = tideType.charAt(0)

              return `
                <div style="text-align: center;">
                  <div style="font-size: 11px; font-weight: 500; color: ${textColor};">${this.y.toFixed(1)} ft</div>
                  <div style="font-size: 9px; color: ${labelColorLight}; margin-top: 2px;">${typeInitial}</div>
                </div>
              `
            }
          }
        }
      ]
    })

    // Cleanup on unmount
    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy()
        chartInstanceRef.current = null
      }
    }
  }, [data, height, width, highchartsLoaded, user])

  return (
    <div className={classNames('common-highcharts-tides-graph', className)}>
      <div ref={chartRef} />
    </div>
  )
}

HighchartsTidesGraph.displayName = 'HighchartsTidesGraph'
